/**
 * SecurityNotice Component
 *
 * 보안 안내 문구를 표시하는 컴포넌트
 * - 서버리스 아키텍처의 보안성을 사용자에게 전달
 * - 로그인/온보딩 화면에서 재사용
 *
 * @example
 * // 기본 사용
 * <SecurityNotice />
 *
 * @example
 * // 커스텀 메시지
 * <SecurityNotice
 *   items={[
 *     { icon: '🔒', text: '커스텀 메시지 1' },
 *     { icon: '🛡️', text: '커스텀 메시지 2' }
 *   ]}
 * />
 */

/**
 * 보안 안내 항목
 */
export interface SecurityNoticeItem {
  /** 아이콘 (이모지) */
  icon: string
  /** 안내 문구 */
  text: string
}

/**
 * SecurityNotice Props
 */
export interface SecurityNoticeProps {
  /** 보안 메시지 항목 (기본값 제공) */
  items?: SecurityNoticeItem[]
  /** 제목 텍스트 (선택사항) */
  title?: string
  /** 스타일 variant */
  variant?: 'default' | 'minimal'
  /** 전체 너비 사용 여부 */
  fullWidth?: boolean
  /** 추가 CSS 클래스 */
  className?: string
}

/**
 * 기본 보안 메시지
 *
 * @why 서버리스 아키텍처의 핵심 보안 특성 3가지를 명확히 전달
 */
const DEFAULT_SECURITY_ITEMS: SecurityNoticeItem[] = [
  {
    icon: '🔒',
    text: '데이터는 귀하의 Google 계정에만 저장되며, 외부 서버에 전송되지 않습니다.'
  },
  {
    icon: '🛡️',
    text: '계정 정보는 로컬 브라우저에만 보관되어 해킹 위험으로부터 안전합니다.'
  },
  {
    icon: '👤',
    text: '귀하만이 스프레드시트에 접근할 수 있으며, 타인의 접근이 불가능합니다.'
  }
]

/**
 * SecurityNotice 컴포넌트
 */
export function SecurityNotice({
  items = DEFAULT_SECURITY_ITEMS,
  title = '🔐 보안 안내',
  variant = 'default',
  fullWidth = true,
  className = ''
}: SecurityNoticeProps) {
  // Variant 스타일
  const variantClasses = {
    default: 'rounded-lg border p-4 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
    minimal: 'p-0'
  }

  // 컨테이너 클래스 조합
  const containerClasses = [
    variantClasses[variant],
    fullWidth ? 'w-full' : '',
    className
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={containerClasses}>
      {/* 제목 (선택사항) */}
      {title && (
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">
          {title}
        </h3>
      )}

      {/* 보안 안내 항목 리스트 */}
      <ul className="space-y-2.5" role="list">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-3">
            {/* 아이콘 */}
            <span
              className="text-xl flex-shrink-0 mt-0.5"
              aria-hidden="true"
            >
              {item.icon}
            </span>

            {/* 텍스트 */}
            <span className="text-xs leading-relaxed text-blue-800 dark:text-blue-200">
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
